/**
 * 메뉴 투표 화면의 콘텐츠 데이터.
 * 출처: _workspace/01_design-analyst_spec.md (§1-2, §1-3, §6-1)
 *
 * - 캐러셀은 이 배열을 기반으로 렌더링한다 (카드 개수 하드코딩 금지).
 * - §6-1: 실제 메뉴는 4건이며 Figma 에는 3장만 캡처됨.
 *   확보된 3건 + 4번째 placeholder 로 구성한다.
 * - §6-1: 활성 카드(소고기삼겹)가 0-based index 1 (페이지네이션 dot-02) 에
 *   오도록 순서를 재정렬한다.
 */

const ASSET_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/menu-vote`;

export interface MenuItem {
  /** 안정적 식별자 */
  id: string;
  /** 메뉴명 */
  name: string;
  /** 음식 사진 경로 (placeholder 인 경우 null) */
  image: string | null;
  /** 사진 원본 픽셀 크기 (CLS 방지용 width/height). placeholder 면 null */
  imageSize: { width: number; height: number } | null;
  /** 정답/추천 메뉴 여부 — UI 배지는 사용자 요청으로 제거됨, 투표 로직용 데이터로 유지 */
  isAnswer: boolean;
  /** 콘텐츠 미확보 placeholder 여부 (§6-1, 4번째 메뉴) */
  isPlaceholder: boolean;
}

/**
 * 배열 순서 = 캐러셀 표시 순서.
 * activeIndex 는 페이지네이션 활성 dot(2번째 = index 1)과 일치한다.
 */
export const MENU_ITEMS: MenuItem[] = [
  {
    id: 'menu-woorong',
    name: '우렁 된장전골',
    image: `${ASSET_BASE}/menu-01-woorong.png`,
    imageSize: { width: 207, height: 468 },
    isAnswer: false,
    isPlaceholder: false,
  },
  {
    // 활성(중앙) 카드 — index 1, 정답 배지
    id: 'menu-sogogi',
    name: '소고기삼겹 된장전골',
    image: `${ASSET_BASE}/menu-03-sogogi.png`,
    imageSize: { width: 346, height: 434 },
    isAnswer: true,
    isPlaceholder: false,
  },
  {
    id: 'menu-pyogo',
    name: '표고버섯 된장전골',
    image: `${ASSET_BASE}/menu-02-pyogo.png`,
    imageSize: { width: 197, height: 468 },
    isAnswer: false,
    isPlaceholder: false,
  },
  {
    // §6-1: 4번째 메뉴는 콘텐츠 미확보 → placeholder/TODO
    // TODO(콘텐츠): 4번째 메뉴 정보(이름/사진) 확보 후 교체 필요
    id: 'menu-all',
    name: '모둠 수육전골',
    image: `${ASSET_BASE}/menu-04-all.png`,
    imageSize: { width: 197, height: 468 }, 
    isAnswer: false,
    isPlaceholder: false,
  },
];

/** 초기 활성 카드 인덱스 (소고기삼겹 = index 1, 페이지네이션 dot-02 와 일치) */
export const INITIAL_ACTIVE_INDEX = 1;

/** 투표 → 결과 전달용 sessionStorage 키 */
export const VOTE_SELECTED_MENU_STORAGE_KEY = 'menu-vote-selected-id';

export function findMenuItemById(id: string): MenuItem | undefined {
  return MENU_ITEMS.find((item) => item.id === id);
}

export const RESTAURANT = {
  name: '옥된장',
  type: '식당',
  logo: `${ASSET_BASE}/restaurant-logo.png`,
  logoSize: { width: 62, height: 40 },
};

export const HERO_COPY = {
  subtitlePlain: '요즘처럼',
  subtitleAccent: ' 쌀쌀한 날',
  titlePlain: '가장 주문하고 싶은',
  titleAccent: '따뜻한 메뉴는?',
  rewardNotice: ['가장 인기 있는 메뉴를 맞추시면,', '리워드를 드립니다!'],
};

/** @deprecated SHARED_HERO_DECORATIONS 사용 — 레이아웃 배경에서 참조 */
export { SHARED_HERO_DECORATIONS as DECORATIONS } from './decorations';

/** Figma 135:5955 Header (detached) — back-button, globe, share, close */
export const HEADER_ICONS = {
  back: `${ASSET_BASE}/header/icon-back-button.svg`,
  globe: `${ASSET_BASE}/header/icon-globe.svg`,
  share: `${ASSET_BASE}/header/icon-share.svg`,
  close: `${ASSET_BASE}/header/icon-close.svg`,
} as const;
